// Helpers for the API requests page (Community API request log).
import moment from 'moment-timezone'

export type StatusClass = 'success' | 'client' | 'server' | 'unknown'

export function statusClass(status: number | null | undefined): StatusClass {
  const s = Number(status)
  if (s >= 200 && s < 400) return 'success'
  if (s >= 400 && s < 500) return 'client'
  if (s >= 500 && s < 600) return 'server'
  return 'unknown'
}

// A request with no status never finished (it timed out or failed part way).
export const STATUS_LABELS: Record<StatusClass, string> = {
  success: 'Success',
  client: 'Client error',
  server: 'Server error',
  unknown: 'No response',
}

// Theme colors: green, orange, pink. tagChipStyle turns them into a soft tint.
export const STATUS_COLORS: Record<StatusClass, string> = {
  success: '#00f2c3',
  client: '#ff8d72',
  server: '#fd5d93',
  unknown: '',
}

// query_params arrives as an object (JSON column) or, defensively, a JSON string.
export function parseQueryParams(raw: any): Record<string, string> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) || {} } catch (e) { return {} }
  }
  return raw
}

export function queryString(raw: any): string {
  const params = parseQueryParams(raw)
  return Object.keys(params).map(k => `${k}=${params[k]}`).join('&')
}

// A stored request or response body for display: indented when it is valid JSON,
// as stored otherwise (a body that was not valid JSON, or one cut at the size cap).
export function formatBody(raw: any): string {
  if (raw === null || raw === undefined || raw === '') return ''
  if (typeof raw !== 'string') return JSON.stringify(raw, null, 2)
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch (e) { return raw }
}

// The date filter holds wall-clock dates in the group's timezone. The server
// compares UTC datetimes, so convert the day bounds to UTC 'YYYY-MM-DD HH:mm:ss'.
export function rangeToUtc(
  value: { mode?: string; days?: string | number; from?: string; to?: string } | null,
  tz: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const fmt = 'YYYY-MM-DD HH:mm:ss'
  const v = value || {}
  if (v.mode === 'days' && Number(v.days) > 0) {
    const end = moment.tz(now, tz).endOf('day')
    const start = moment.tz(now, tz).subtract(Number(v.days) - 1, 'days').startOf('day')
    return { from: start.utc().format(fmt), to: end.utc().format(fmt) }
  }
  if (v.mode === 'range' && v.from && v.to) {
    return {
      from: moment.tz(v.from, 'YYYY-MM-DD', tz).startOf('day').utc().format(fmt),
      to: moment.tz(v.to, 'YYYY-MM-DD', tz).endOf('day').utc().format(fmt),
    }
  }
  return { from: '', to: '' }
}
